import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, Search, ShoppingBag, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { SearchOverlay } from "@/components/SearchOverlay";
import { ease } from "@/components/Reveal";

const BRANDS = [
  ["nike", "Nike"], ["air-jordan", "Air Jordan"], ["jordan-retro", "Jordan Retro"], ["adidas", "Adidas"],
  ["new-balance", "New Balance"], ["puma", "Puma"], ["asics", "Asics"],
];
const EDITS = [["running", "Running"], ["lifestyle", "Lifestyle"], ["streetwear", "Streetwear"]];

function LangToggle({ dark }) {
  const { lang, setLang } = useStore();
  return (
    <div className="flex items-center font-mono text-[11px] tracking-[0.2em]" data-testid="lang-toggle">
      {["en", "pt"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className={dark ? "text-sun/30" : "text-ink/30"}>/</span>}
          <button
            data-testid={`lang-${l}-btn`}
            onClick={() => setLang(l)}
            className={`px-1.5 uppercase transition-opacity ${lang === l ? "opacity-100" : "opacity-40 hover:opacity-80"}`}
          >{l}</button>
        </span>
      ))}
    </div>
  );
}

function MegaMenu({ onClose }) {
  const { t } = useStore();
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease }}
      className="absolute left-0 right-0 top-full border-b border-concrete bg-sun text-ink"
      data-testid="mega-menu"
    >
      <div className="container-x grid grid-cols-12 gap-10 py-12">
        <div className="col-span-5">
          <p className="eyebrow mb-6 text-ink/50">{t("brands")}</p>
          <div className="grid grid-cols-2 gap-y-3">
            {BRANDS.map(([s, n]) => (
              <Link key={s} to={`/collections/${s}`} onClick={onClose} data-testid={`mega-brand-${s}`}
                className="font-display text-2xl font-bold uppercase tracking-tight transition-transform duration-300 hover:translate-x-1">{n}</Link>
            ))}
          </div>
        </div>
        <div className="col-span-3">
          <p className="eyebrow mb-6 text-ink/50">{t("collections")}</p>
          <div className="flex flex-col gap-3">
            {EDITS.map(([s, n]) => (
              <Link key={s} to={`/collections/${s}`} onClick={onClose} data-testid={`mega-edit-${s}`} className="link-u w-fit text-lg">{n}</Link>
            ))}
            <Link to="/shop?is_new=1" onClick={onClose} className="link-u w-fit text-lg">{t("newIn")}</Link>
            <Link to="/shop?on_sale=1" onClick={onClose} className="link-u w-fit text-lg text-ink">{t("sale")}</Link>
          </div>
        </div>
        <Link to="/collections/air-jordan" onClick={onClose} className="group relative col-span-4 aspect-[4/3] overflow-hidden bg-concrete">
          <img src="https://images.unsplash.com/photo-1552346154-21d32810aba3?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" alt="Air Jordan 1"
            className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
          <p className="absolute bottom-5 left-5 font-display text-2xl font-black uppercase text-sun">{t("jordanCulture")}</p>
        </Link>
      </div>
    </motion.div>
  );
}

export function Header() {
  const { t, count, setCartOpen, wishlist } = useStore();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [search, setSearch] = useState(false);
  const overHero = false;
  const hideLogo = pathname === "/" && !scrolled && !mega;

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setMega(false); setMobile(false); }, [pathname]);

  const navCls = ({ isActive }) => `link-u whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.16em] ${isActive ? "opacity-100" : "opacity-80 hover:opacity-100"}`;

  return (
    <>
      <div className="bg-ink py-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-sun/80" data-testid="announcement-bar">
        {t("announce")}
      </div>
      <header
        onMouseLeave={() => setMega(false)}
        className={`sticky top-0 z-50 transition-[background-color,color,border-color] duration-500 ${overHero
          ? "" : `border-b bg-sun/90 text-ink backdrop-blur-xl ${hideLogo ? "border-transparent" : "border-ink/10"}`}`}
        data-testid="site-header"
      >
        <div className="container-x grid h-16 grid-cols-[1fr_auto_1fr] items-center lg:h-20">
          <div className="flex items-center gap-8">
            <button className="xl:hidden" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <nav className="hidden items-center gap-5 xl:flex">
            <button onMouseEnter={() => setMega(true)} onClick={() => setMega((m) => !m)} data-testid="nav-brands-btn"
              className="link-u whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.16em] opacity-80 hover:opacity-100">{t("brands")}</button>
            <NavLink to="/shop" end className={navCls} data-testid="nav-shop">{t("shop")}</NavLink>
            <NavLink to="/collections/air-jordan" className={navCls} data-testid="nav-jordan">{t("jordan")}</NavLink>
            <NavLink to="/collections/nike" className={navCls} data-testid="nav-nike">Nike</NavLink>
            <NavLink to="/collections" end className={(a) => `${navCls(a)} hidden 2xl:inline`} data-testid="nav-collections">{t("collections")}</NavLink>
            <NavLink to="/categories" end className={navCls} data-testid="nav-shop-by-category">{t("shopByCategory")}</NavLink>
            <Link to="/shop?on_sale=1" className="link-u whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-ink" data-testid="nav-sale">{t("sale")}</Link>
          </nav>
          </div>
          <Link to="/" className={`wordmark text-[15px] tracking-[0.1em] transition-[opacity,transform] duration-500 sm:text-2xl sm:tracking-[0.14em] lg:text-[28px] ${hideLogo ? "pointer-events-none -translate-y-2 opacity-0" : "opacity-100"}`} data-testid="logo-link">CASABLANCA</Link>
          <div className="flex items-center justify-end gap-3 sm:gap-5">
            <div className="hidden sm:block"><LangToggle dark={overHero} /></div>
            <button onClick={() => setSearch(true)} aria-label={t("search")} data-testid="search-open-btn"><Search className="h-5 w-5" strokeWidth={1.6} /></button>
            <Link to="/wishlist" aria-label={t("wishlist")} className="relative" data-testid="wishlist-link">
              <Heart className="h-5 w-5" strokeWidth={1.6} />
              {wishlist.length > 0 && <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-lavender px-1 text-[9px] font-bold text-ink">{wishlist.length}</span>}
            </Link>
            <button onClick={() => setCartOpen(true)} aria-label={t("bag")} className="relative" data-testid="cart-open-btn">
              <ShoppingBag className="h-5 w-5" strokeWidth={1.6} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span key={count} initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} data-testid="cart-count"
                    className={`absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold ${overHero ? "bg-sun text-ink" : "bg-ink text-sun"}`}>{count}</motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
        <AnimatePresence>{mega && <MegaMenu onClose={() => setMega(false)} />}</AnimatePresence>
      </header>

      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-full max-w-none border-0 bg-ink p-0 text-sun sm:max-w-md [&>button]:hidden" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex h-16 items-center justify-between px-5">
            <span className="wordmark text-xl text-sun">CASABLANCA</span>
            <button onClick={() => setMobile(false)} aria-label="Close" data-testid="mobile-menu-close"><X className="h-6 w-6" /></button>
          </div>
          <div className="flex flex-col gap-1 overflow-y-auto px-5 pb-10 pt-6">
            {[["/shop", t("allProducts")], ["/categories", t("shopByCategory")], ...BRANDS.map(([s, n]) => [`/collections/${s}`, n]), ...EDITS.map(([s, n]) => [`/collections/${s}`, n]),
              ["/shop?is_new=1", t("newIn")], ["/shop?on_sale=1", t("sale")]].map(([to, label], i) => (
              <motion.div key={to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i, ease, duration: 0.5 }}>
                <Link to={to} onClick={() => setMobile(false)} className="block border-b border-sun/10 py-3 font-display text-3xl font-black uppercase tracking-tight"
                  data-testid={`mobile-link-${i}`}>{label}</Link>
              </motion.div>
            ))}
            <div className="mt-8"><LangToggle dark /></div>
          </div>
        </SheetContent>
      </Sheet>
      <SearchOverlay open={search} onClose={() => setSearch(false)} />
    </>
  );
}
